/**
 * Tipi TypeScript per Admin API
 * Corrispondono agli schemi Pydantic del backend
 */

import { UserRole } from './auth';

/**
 * Company Type enum
 */
export enum CompanyType {
  SENDER = 'sender',
  RECEIVER = 'receiver',
  FREIGHT_FORWARDER = 'freight_forwarder',
  PORT_AUTHORITY = 'port_authority',
  CARRIER = 'carrier',
  CUSTOMS_BROKER = 'customs_broker',
  INSURANCE_PROVIDER = 'insurance_provider',
}

/**
 * User Response
 */
export interface UserResponse {
  user_id: string;
  company_id: string | null;
  email: string;
  email_verified: boolean;
  email_verified_at: string | null;
  first_name: string | null;
  last_name: string | null;
  full_name: string;
  user_role: UserRole;
  permissions: Record<string, unknown> | null;
  phone: string | null;
  timezone: string;
  preferred_language: string;
  last_login_at: string | null;
  last_login_ip: string | null;
  avatar_url: string | null;
  job_title: string | null;
  department: string | null;
  notification_preferences: Record<string, unknown> | null;
  is_active: boolean;
  is_2fa_enabled: boolean;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

/**
 * User Create Request
 */
export interface UserCreate {
  email: string;
  password: string;
  company_id?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  user_role?: UserRole;
  phone?: string | null;
  timezone?: string;
  preferred_language?: string;
  job_title?: string | null;
  department?: string | null;
  is_active?: boolean;
}

/**
 * User Update Request
 */
export interface UserUpdate {
  email?: string;
  password?: string;
  first_name?: string | null;
  last_name?: string | null;
  user_role?: UserRole;
  phone?: string | null;
  timezone?: string;
  preferred_language?: string;
  job_title?: string | null;
  department?: string | null;
  is_active?: boolean;
}

/**
 * User List Filters
 */
export interface UserListFilters {
  skip?: number;
  limit?: number;
  company_id?: string;
  user_role?: UserRole;
  is_active?: boolean;
}

/**
 * Company Response
 */
export interface CompanyResponse {
  company_id: string;
  company_name: string;
  company_type: CompanyType;
  legal_name: string | null;
  tax_id: string | null;
  vat_number: string | null;
  eori_number: string | null;
  trading_license_number: string | null;
  headquarters_address: Record<string, unknown> | null;
  primary_email: string | null;
  primary_phone: string | null;
  website: string | null;
  industry: string | null;
  company_size: string | null;
  year_established: number | null;
  onboarding_status: string;
  is_active: boolean;
  is_verified: boolean;
  verified_at: string | null;
  notes: string | null;
  /** Shipper-declared HS codes, routes, carriers (JSON) */
  operational_profile?: {
    hs_codes?: string[];
    trade_routes?: Array<{
      origin_port_locode?: string;
      destination_port_locode?: string;
      origin_country_code?: string;
      destination_country_code?: string;
      note?: string;
    }>;
    carrier_scacs?: string[];
  } | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

/**
 * Company Create Request
 */
export interface CompanyCreate {
  company_name: string;
  company_type: CompanyType;
  legal_name?: string | null;
  tax_id?: string | null;
  vat_number?: string | null;
  eori_number?: string | null;
  trading_license_number?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state_province?: string | null;
  postal_code?: string | null;
  country_code?: string | null;
  primary_email?: string | null;
  primary_phone?: string | null;
  website?: string | null;
  industry?: string | null;
  company_size?: string | null;
  year_established?: number | null;
  is_active?: boolean;
  notes?: string | null;
}

/**
 * Company Update Request
 */
export interface CompanyUpdate {
  company_name?: string;
  company_type?: CompanyType;
  legal_name?: string | null;
  tax_id?: string | null;
  vat_number?: string | null;
  eori_number?: string | null;
  trading_license_number?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state_province?: string | null;
  postal_code?: string | null;
  country_code?: string | null;
  primary_email?: string | null;
  primary_phone?: string | null;
  website?: string | null;
  industry?: string | null;
  company_size?: string | null;
  year_established?: number | null;
  is_active?: boolean;
  is_verified?: boolean;
  notes?: string | null;
}

/**
 * Company List Filters
 */
export interface CompanyListFilters {
  skip?: number;
  limit?: number;
  company_type?: CompanyType;
  is_active?: boolean;
}

/**
 * Company Settings Response
 */
export interface CompanySettings {
  company_settings_id: string;
  company_id: string;
  default_currency?: string | null;
  default_language?: string | null;
  timezone?: string | null;
  date_format?: string | null;
  auto_extract_documents?: boolean | null;
  auto_validate_documents?: boolean | null;
  notification_email?: string | null;
  email_notifications_enabled?: boolean | null;
  slack_notifications_enabled?: boolean | null;
  webhook_url?: string | null;
  api_rate_limit?: number | null;
  // Tracking settings
  tracking_auto_refresh_enabled?: boolean;
  tracking_refresh_time?: string;           // "HH:MM" format (24-hour UTC)
  tracking_refresh_times_per_day?: number;  // Read-only, always 1 for MVP
  tracking_cache_ttl_hours?: number;
  // Mail Companion settings
  mail_companion_enabled?: boolean;
  mail_companion_sender_domains?: string[];
  mail_companion_notify_events?: string[];
  mail_companion_reply_locale?: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

/**
 * Company Settings Update Request
 */
export interface CompanySettingsUpdate {
  default_currency?: string | null;
  default_language?: string | null;
  timezone?: string | null;
  date_format?: string | null;
  auto_extract_documents?: boolean | null;
  auto_validate_documents?: boolean | null;
  notification_email?: string | null;
  email_notifications_enabled?: boolean | null;
  slack_notifications_enabled?: boolean | null;
  webhook_url?: string | null;
  api_rate_limit?: number | null;
  // Tracking settings
  tracking_auto_refresh_enabled?: boolean;
  tracking_refresh_time?: string;
  // Mail Companion settings
  mail_companion_enabled?: boolean;
  mail_companion_sender_domains?: string[];
  mail_companion_notify_events?: string[];
  mail_companion_reply_locale?: string;
}

/**
 * User Session Response
 */
export interface UserSession {
  session_id: string;
  user_id: string;
  session_token: string;
  expires_at: string;
  revoked_at: string | null;
  revoke_reason: string | null;
  user_agent: string | null;
  ip_address: string | null;
  last_activity_at: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

/**
 * User Session List Filters
 */
export interface UserSessionListFilters {
  skip?: number;
  limit?: number;
  user_id?: string;
  is_active?: boolean;
}

/**
 * Delete Response
 */
export interface DeleteResponse {
  message: string;
  user_id?: string;
  company_id?: string;
  deleted_at: string;
}

